import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Trans } from "@lingui/react/macro";

import ConfiguredSensorAlarmCard from "../components/ConfiguredSensorAlarmCard";
import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import {
  AlarmSeverity,
  ComparisonDirection,
  NotificationChannel,
  SensorDataTrigger,
} from "../types/alarmInterface";
import { IData, IDevice } from "../types/deviceInterface";

interface DeviceOption {
  id: number;
  device_name: string;
}

const metricDataKeys = {
  MOISTURE: "soil_moisture",
  MOISTURE_2: "soil_moisture_2",
  MOISTURE_3: "soil_moisture_3",
  TEMPERATURE: "air_temperature",
  HUMIDITY: "humidity",
  SOIL_TEMPERATURE: "soil_temperature",
  BATTERY_LEVEL: "battery_voltage",
} as const;

const metricLabels = {
  MOISTURE: "Moisture",
  MOISTURE_2: "Moisture 2",
  MOISTURE_3: "Moisture 3",
  TEMPERATURE: "Temperature",
  HUMIDITY: "Humidity",
  SOIL_TEMPERATURE: "Soil temperature",
  BATTERY_LEVEL: "Battery voltage",
} as const;

type Metric = keyof typeof metricDataKeys;
const channelOptions: NotificationChannel[] = ["IN_APP", "EMAIL", "WHATSAPP", "PUSH"];

const AddEditAlarmView = () => {
  const api = useAxios();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [currentData, setCurrentData] = useState<IData | null>(null);
  const [configuredTriggers, setConfiguredTriggers] = useState<SensorDataTrigger[]>([]);
  const [loadingCurrentData, setLoadingCurrentData] = useState(false);
  const [form, setForm] = useState({
    device: searchParams.get("device") || "",
    metric: "MOISTURE",
    trigger_value: "",
    comparison_direction: "BELOW" as ComparisonDirection,
    severity: "WARNING" as AlarmSeverity,
    notification_channels: ["IN_APP", "EMAIL"] as NotificationChannel[],
  });

  useEffect(() => {
    api
      .get<DeviceOption[]>("/frontend/user-devices/")
      .then((response) => setDevices(response.data))
      .catch(() =>
        toast({ title: <Trans>Error fetching devices</Trans>, status: "error", isClosable: true }),
      );

    if (id) {
      api
        .get<SensorDataTrigger>(`/frontend/alarm-triggers/sensor-data/${id}/`)
        .then(({ data }) =>
          setForm({
            device: String(data.device),
            metric: data.metric,
            trigger_value: String(data.trigger_value),
            comparison_direction: data.comparison_direction,
            severity: data.severity,
            notification_channels: data.notification_channels,
          }),
        )
        .catch(() =>
          toast({
            title: <Trans>Error fetching alarm trigger</Trans>,
            status: "error",
            isClosable: true,
          }),
        );
    }
  }, [id]);

  useEffect(() => {
    if (!form.device) {
      setCurrentData(null);
      return;
    }

    setLoadingCurrentData(true);
    Promise.all([
      api.get<IDevice>(`/frontend/devices/current-data/${form.device}/`),
      api.get<SensorDataTrigger[]>("/frontend/alarm-triggers/sensor-data/"),
    ])
      .then(([{ data }, triggerResponse]) => {
        const nextData = data.data || null;
        setCurrentData(nextData);
        setConfiguredTriggers(
          triggerResponse.data.filter(
            (trigger) => trigger.device === Number(form.device) && trigger.id !== Number(id),
          ),
        );
        const availableMetrics = (Object.keys(metricDataKeys) as Metric[]).filter(
          (metric) => typeof nextData?.[metricDataKeys[metric] as keyof IData] === "number",
        );
        if (availableMetrics.length && !availableMetrics.includes(form.metric as Metric)) {
          setForm((previous) => ({ ...previous, metric: availableMetrics[0] }));
        }
      })
      .catch(() => {
        setCurrentData(null);
        setConfiguredTriggers([]);
        toast({
          title: <Trans>Unable to load current device data</Trans>,
          status: "warning",
          isClosable: true,
        });
      })
      .finally(() => setLoadingCurrentData(false));
  }, [form.device]);

  const save = async () => {
    try {
      const payload = {
        ...form,
        device: Number(form.device),
        trigger_value: Number(form.trigger_value),
      };
      if (id) {
        await api.put(`/frontend/alarm-triggers/sensor-data/${id}/`, payload);
      } else {
        await api.post("/frontend/alarm-triggers/sensor-data/", payload);
      }
      toast({
        title: id ? <Trans>Alarm trigger updated</Trans> : <Trans>Alarm trigger created</Trans>,
        status: "success",
        isClosable: true,
      });
      navigate("/alarms");
    } catch {
      toast({
        title: <Trans>Unable to save alarm trigger</Trans>,
        status: "error",
        isClosable: true,
      });
    }
  };

  const availableMetrics = (Object.keys(metricDataKeys) as Metric[]).filter(
    (metric) => typeof currentData?.[metricDataKeys[metric] as keyof IData] === "number",
  );
  const selectedMetric = form.metric as Metric;
  const selectedCurrentValue = currentData?.[metricDataKeys[selectedMetric] as keyof IData];
  const canSave = Boolean(
    form.device &&
    availableMetrics.includes(selectedMetric) &&
    form.trigger_value &&
    form.notification_channels.length,
  );

  const removeConfiguredTrigger = async (trigger: SensorDataTrigger) => {
    try {
      await api.delete(`/frontend/alarm-triggers/sensor-data/${trigger.id}/`);
      setConfiguredTriggers((triggers) => triggers.filter(({ id }) => id !== trigger.id));
    } catch {
      toast({ title: <Trans>Unable to delete alarm</Trans>, status: "error", isClosable: true });
    }
  };

  return (
    <Navbar>
      <Box maxW="760px" mx="auto" py={8} px={4}>
        <Heading size="lg" mb={2}>
          {id ? <Trans>Edit alarm trigger</Trans> : <Trans>Add alarm</Trans>}
        </Heading>
        <Text color="gray.500" mb={6}>
          <Trans>Configure when this device should create an alarm.</Trans>
        </Text>
        <VStack align="stretch" spacing={5}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>
                <Trans>Device</Trans>
              </FormLabel>
              <Select
                value={form.device}
                onChange={(event) => setForm({ ...form, device: event.target.value })}
              >
                <option value="">
                  <Trans>Select device</Trans>
                </option>
                {devices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.device_name || `Device ${device.id}`}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Trans>Measurement</Trans>
              </FormLabel>
              <Select
                value={form.metric}
                onChange={(event) => setForm({ ...form, metric: event.target.value })}
                isDisabled={!form.device || loadingCurrentData || !availableMetrics.length}
              >
                {!availableMetrics.length && (
                  <option value="">
                    <Trans>Select a device first</Trans>
                  </option>
                )}
                {availableMetrics.map((metric) => (
                  <option key={metric} value={metric}>
                    {metricLabels[metric]}
                  </option>
                ))}
              </Select>
              {form.device && !loadingCurrentData && typeof selectedCurrentValue === "number" && (
                <Text mt={2} fontSize="sm" color="gray.600">
                  <Trans>Current value:</Trans> {selectedCurrentValue.toFixed(1)}
                  {currentData?.timestamp &&
                    ` (${new Date(currentData.timestamp).toLocaleString()})`}
                </Text>
              )}
              {form.device && !loadingCurrentData && !availableMetrics.length && (
                <Text mt={2} fontSize="sm" color="orange.600">
                  <Trans>This device has no supported current measurements to configure.</Trans>
                </Text>
              )}
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Trans>Trigger when value is</Trans>
              </FormLabel>
              <Select
                value={form.comparison_direction}
                onChange={(event) =>
                  setForm({
                    ...form,
                    comparison_direction: event.target.value as ComparisonDirection,
                  })
                }
              >
                <option value="BELOW">
                  <Trans>Below</Trans>
                </option>
                <option value="ABOVE">
                  <Trans>Above</Trans>
                </option>
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Trans>Threshold</Trans>
              </FormLabel>
              <Input
                type="number"
                value={form.trigger_value}
                onChange={(event) => setForm({ ...form, trigger_value: event.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel>
                <Trans>Severity</Trans>
              </FormLabel>
              <Select
                value={form.severity}
                onChange={(event) =>
                  setForm({ ...form, severity: event.target.value as AlarmSeverity })
                }
              >
                <option value="WARNING">
                  <Trans>Warning</Trans>
                </option>
                <option value="ERROR">
                  <Trans>Error</Trans>
                </option>
              </Select>
            </FormControl>
          </SimpleGrid>
          <FormControl isRequired>
            <FormLabel>
              <Trans>Notifications</Trans>
            </FormLabel>
            <CheckboxGroup
              value={form.notification_channels}
              onChange={(values) =>
                setForm({ ...form, notification_channels: values as NotificationChannel[] })
              }
            >
              <Stack direction={{ base: "column", md: "row" }} spacing={5}>
                {channelOptions.map((channel) => (
                  <Checkbox
                    key={channel}
                    value={channel}
                    isDisabled={channel === "WHATSAPP" || channel === "PUSH"}
                  >
                    {channel.replaceAll("_", " ")}
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          </FormControl>
          <Button
            alignSelf="start"
            colorScheme="back"
            variant={"outline"}
            borderRadius={"0"}
            onClick={save}
            isDisabled={!canSave}
          >
            <Trans>Save alarm</Trans>
          </Button>

          {form.device && (
            <Box pt={4} borderTopWidth="1px">
              <Heading size="sm" mb={1}>
                <Trans>Configured Alarm Triggers</Trans>
              </Heading>
              <Text color="gray.500" fontSize="sm" mb={3}>
                <Trans>Configured alarm triggers for the selected device.</Trans>
              </Text>
              <VStack align="stretch" spacing={2}>
                {configuredTriggers.map((trigger) => (
                  <ConfiguredSensorAlarmCard
                    key={trigger.id}
                    trigger={trigger}
                    onDelete={removeConfiguredTrigger}
                  />
                ))}
                {!loadingCurrentData && !configuredTriggers.length && (
                  <Text color="gray.500" fontSize="sm">
                    <Trans>No alarm rules are configured for this device.</Trans>
                  </Text>
                )}
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>
    </Navbar>
  );
};

export default AddEditAlarmView;
